import {
    useEffect,
    useMemo,
    useRef
} from "react";
import {
    Animated,
    Platform
} from "react-native";
import type {
    PopoverSurfaceMotionHookConstantType,
    PopoverSurfaceMotionHookType
} from "./type";
import {
    getPopoverSurfaceMotion
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    getMotionDuration,
    useMotionTiming,
    MOTION_INTENTS
} from "../../helpers/motion";

export const usePopoverSurfaceMotion = ({
    customTheme,
    actualSide,
    isRendered,
    onExited,
    isReady,
    isOpen
}: PopoverSurfaceMotionHookConstantType): PopoverSurfaceMotionHookType => {
    const {
        durations,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const enterTiming = useMotionTiming("surfaceEnter", customTheme);

    const exitTiming = useMotionTiming("surfaceExit", customTheme);

    const opacity = useRef(new Animated.Value(0)).current;

    const pop = useRef(new Animated.Value(0)).current;

    const onExitedRef = useRef(onExited);

    const isEnteredRef = useRef(false);

    const isNativeDriver = Platform.OS !== "web";

    const fadeDuration = getMotionDuration({
        isReduceMotionEnabled: enterTiming.isReduceMotionEnabled,
        duration: MOTION_INTENTS.surfaceEnter.duration,
        motion: "fade",
        durations
    });

    const motion = useMemo(() => getPopoverSurfaceMotion({
        actualSide,
        spaces,
        pop
    }), [
        actualSide,
        spaces,
        pop
    ]);

    onExitedRef.current = onExited;

    useEffect(() => {
        if(!isRendered || (isOpen && !isReady)) {
            return;
        }

        if(!isOpen) {
            const closeAnimation = Animated.timing(opacity, {
                useNativeDriver: isNativeDriver,
                duration: exitTiming.duration,
                easing: exitTiming.easing,
                isInteraction: false,
                toValue: 0
            });

            closeAnimation.start(({
                finished
            }) => {
                if(!finished) {
                    return;
                }

                isEnteredRef.current = false;

                onExitedRef.current?.();
            });

            return () => {
                closeAnimation.stop();
            };
        }

        if(!isEnteredRef.current) {
            pop.setValue(0);
        }

        isEnteredRef.current = true;

        const openAnimation = Animated.parallel([
            Animated.timing(opacity, {
                useNativeDriver: isNativeDriver,
                easing: enterTiming.easing,
                duration: fadeDuration,
                isInteraction: false,
                toValue: 1
            }),
            Animated.timing(pop, {
                useNativeDriver: isNativeDriver,
                duration: enterTiming.duration,
                easing: enterTiming.easing,
                isInteraction: false,
                toValue: 1
            })
        ]);

        openAnimation.start();

        return () => {
            openAnimation.stop();
        };
    }, [
        isRendered,
        isReady,
        isOpen
    ]);

    return {
        opacity,
        motion
    };
};
